import React, { useState, useEffect } from 'react';
import * as Realm from "realm-web";

const app = new Realm.App({ id: "thebrainfund-qyrogsg" });

function CommunityModels({ onSelectModel }) {
  const [sortBy, setSortBy] = useState('performance');
  const [filterBy, setFilterBy] = useState('all');
  const [previewModel, setPreviewModel] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTab, setPreviewTab] = useState('details'); // New state for preview tabs
  const [communityModels, setCommunityModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch models from MongoDB Atlas
  const fetchModels = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await app.currentUser.functions.getModels({
        sortBy: sortBy,
        filterBy: filterBy,
        limit: 50
      });
      
      if (result.success) {
        setCommunityModels(result.models);
      } else {
        setError(result.error || 'Failed to fetch models');
        setCommunityModels([]);
      }
    } catch (err) {
      console.error('Error fetching community models:', err);
      setError('Failed to connect to server');
      setCommunityModels([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch models when component mounts or filters change
  useEffect(() => {
    fetchModels();
  }, [sortBy, filterBy]);

  const getRatingStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <span className="rating-stars">
        {'⭐'.repeat(fullStars)}
        {hasHalfStar && '⭐'}
        {'☆'.repeat(emptyStars)}
      </span>
    );
  };

  const getPerformanceColor = (roi) => {
    if (roi >= 15) return '#10b981';
    if (roi >= 10) return '#3b82f6';
    if (roi >= 5) return '#f59e0b';
    return '#ef4444';
  };

  const handlePreview = (model) => {
    setPreviewModel(model);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewModel(null);
    setPreviewTab('details'); // Reset to details tab when closing
  };

  // Mock reviews data for the selected model
  const getModelReviews = (modelName) => {
    const allReviews = [
      {
        id: '1',
        modelName: 'NBA Value Hunter',
        reviewerName: 'SportsBettor87',
        rating: 5,
        title: 'Excellent model for NBA betting!',
        comment: 'This model has been incredibly accurate for NBA games. Made consistent profits over the past month.',
        date: new Date('2025-08-01'),
        verified: true,
        helpful: 24
      },
      {
        id: '2',
        modelName: 'Weather Warrior',
        reviewerName: 'BettingExpert',
        rating: 4,
        title: 'Great for outdoor sports',
        comment: 'Really good at predicting how weather affects game outcomes. Especially useful for football and baseball.',
        date: new Date('2025-07-28'),
        verified: true,
        helpful: 18
      },
      {
        id: '3',
        modelName: 'NBA Value Hunter',
        reviewerName: 'CasualBettor',
        rating: 4,
        title: 'Good results but takes time to learn',
        comment: 'The model works well but you need to understand the variables. Worth the learning curve.',
        date: new Date('2025-07-25'),
        verified: false,
        helpful: 12
      },
      {
        id: '4',
        modelName: 'The Contrarian',
        reviewerName: 'SharpMoney',
        rating: 3,
        title: 'Too complex for beginners',
        comment: 'The model works but requires understanding of line movements and sharp money. Not for casual bettors.',
        date: new Date('2025-07-20'),
        verified: false,
        helpful: 7
      }
    ];
    
    return allReviews.filter(review => review.modelName === modelName);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return '#10b981';
    if (rating >= 3.5) return '#3b82f6';
    if (rating >= 2.5) return '#f59e0b';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div className="community-models">
        <div className="community-header">
          <h2>Community Models</h2>
          <p>Discover and use models created by other traders in the community</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading community models...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="community-models">
        <div className="community-header">
          <h2>Community Models</h2>
          <p>Discover and use models created by other traders in the community</p>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Models</h3>
          <p>{error}</p>
          <button className="btn-primary" onClick={fetchModels}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="community-models">
      <div className="community-header">
        <h2>Community Models</h2>
        <p>Discover and use models created by other traders in the community</p>
      </div>

      {/* Filters and Sorting */}
      <div className="community-controls">
        <div className="filter-group">
          <label>Filter by:</label>
          <select 
            value={filterBy} 
            onChange={(e) => setFilterBy(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Models</option>
            <option value="free">Free Models</option>
            <option value="premium">Premium Models</option>
            <option value="nba">NBA</option>
            <option value="nfl">NFL</option>
            <option value="soccer">Soccer</option>
            <option value="mlb">MLB</option>
            <option value="nhl">NHL</option>
          </select>
        </div>

        <div className="sort-group">
          <label>Sort by:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="performance">Performance (ROI)</option>
            <option value="popularity">Popularity</option>
            <option value="rating">Rating</option>
            <option value="newest">Newest</option>
            <option value="price_low">Price (Low to High)</option>
            <option value="price_high">Price (High to Low)</option>
          </select>
        </div>
      </div>

      {/* Models Grid */}
      <div className="models-grid">
        {communityModels.map(model => (
          <div key={model.id} className="community-model-card">
            <div className="model-card-header">
              <div className="model-title-section">
                <h3 className="model-title">{model.name}</h3>
                <div className="model-meta">
                  <span className="model-author">by {model.author}</span>
                  <div className="author-rating">
                    {getRatingStars(model.authorRating)}
                    <span className="rating-value">({model.authorRating})</span>
                  </div>
                </div>
              </div>
              <div className="model-price">
                {model.price === 0 ? (
                  <span className="price-free">FREE</span>
                ) : (
                  <span className="price-paid">${model.price}</span>
                )}
              </div>
            </div>

            <p className="model-description">{model.description}</p>

            <div className="model-tags">
              {model.tags.map(tag => (
                <span key={tag} className="model-tag">{tag}</span>
              ))}
            </div>

            <div className="model-performance-summary">
              <div className="perf-item">
                <span className="perf-label">Win Rate</span>
                <span className="perf-value">{model.performance.winRate}%</span>
              </div>
              <div className="perf-item">
                <span className="perf-label">ROI</span>
                <span 
                  className="perf-value"
                  style={{ color: getPerformanceColor(model.performance.roi) }}
                >
                  +{model.performance.roi}%
                </span>
              </div>
              <div className="perf-item">
                <span className="perf-label">Bets</span>
                <span className="perf-value">{model.performance.totalBets}</span>
              </div>
            </div>

            <div className="model-stats">
              <div className="stat-item">
                <span className="stat-icon">⭐ Reviews</span>
                <span>{model.rating} ({model.reviews} reviews)</span>
              </div>
              <div className="stat-item">
                <span className="stat-icon">📥 Downloads</span>
                <span>{model.downloads}</span>
              </div>
              <div className="stat-item">
                <span className="stat-icon">📅 Created</span>
                <span>{new Date(model.created).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="model-variables-preview">
              <span className="variables-label">Key Variables:</span>
              <div className="variables-mini">
                {model.variables.slice(0, 3).map(variable => (
                  <span key={variable.id} className="variable-mini">
                    {variable.name} ({variable.weight}%)
                  </span>
                ))}
                {model.variables.length > 3 && (
                  <span className="variables-more">+{model.variables.length - 3} more</span>
                )}
              </div>
            </div>

            <div className="model-actions">
              <button 
                className="btn-secondary model-preview-btn"
                onClick={() => handlePreview(model)}
              >
                📊 Preview
              </button>
              <button 
                className="btn-primary model-use-btn"
                onClick={() => onSelectModel(model)}
              >
                {model.price === 0 ? '📥 Use Model' : '💳 Purchase & Use'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {communityModels.length === 0 && !loading && (
        <div className="no-models">
          <div className="no-models-icon">🔍</div>
          <h3>No Models Found</h3>
          <p>Try adjusting your filters to see more models</p>
        </div>
      )}

      {/* Model Preview Modal */}
      {showPreview && previewModel && (
        <div className="preview-modal-overlay" onClick={closePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <div className="preview-title-section">
                <h2>{previewModel.name}</h2>
                <span className="preview-author">by {previewModel.author}</span>
              </div>
              <button className="preview-close" onClick={closePreview}>✕</button>
            </div>
            
            {/* Preview Tabs */}
            <div className="preview-tabs">
              <button 
                className={`preview-tab ${previewTab === 'details' ? 'active' : ''}`}
                onClick={() => setPreviewTab('details')}
              >
                📊 Details
              </button>
              <button 
                className={`preview-tab ${previewTab === 'reviews' ? 'active' : ''}`}
                onClick={() => setPreviewTab('reviews')}
              >
                ⭐ Reviews ({getModelReviews(previewModel.name).length})
              </button>
            </div>
            
            <div className="preview-content">
              {previewTab === 'details' && (
                <>
                  <div className="preview-section">
                    <h3>📊 Performance Overview</h3>
                    <div className="preview-performance-grid">
                      <div className="preview-metric">
                        <span className="preview-metric-label">Win Rate</span>
                        <span className="preview-metric-value">{previewModel.performance.winRate}%</span>
                      </div>
                      <div className="preview-metric">
                        <span className="preview-metric-label">ROI</span>
                        <span 
                          className="preview-metric-value"
                          style={{ color: getPerformanceColor(previewModel.performance.roi) }}
                        >
                          +{previewModel.performance.roi}%
                        </span>
                      </div>
                      <div className="preview-metric">
                        <span className="preview-metric-label">Total Bets</span>
                        <span className="preview-metric-value">{previewModel.performance.totalBets}</span>
                      </div>
                      <div className="preview-metric">
                        <span className="preview-metric-label">Profit</span>
                        <span className="preview-metric-value">${previewModel.performance.profit}</span>
                      </div>
                    </div>
                  </div>

                  <div className="preview-section">
                    <h3>🔧 Model Variables</h3>
                    <div className="preview-variables">
                      {previewModel.variables.map(variable => (
                        <div key={variable.id} className="preview-variable">
                          <div className="preview-variable-info">
                            <span className="preview-variable-name">{variable.name}</span>
                            <span className="preview-variable-weight">{variable.weight}%</span>
                          </div>
                          <div className="preview-variable-bar">
                            <div 
                              className="preview-variable-fill"
                              style={{ width: `${variable.weight}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="preview-section">
                    <h3>📝 Strategy Description</h3>
                    <p className="preview-description">{previewModel.description}</p>
                  </div>

                  <div className="preview-section">
                    <h3>🏷️ Tags & Category</h3>
                    <div className="preview-tags">
                      {previewModel.tags.map(tag => (
                        <span key={tag} className="preview-tag">{tag}</span>
                      ))}
                    </div>
                  </div>

                  <div className="preview-section">
                    <h3>📈 Community Stats</h3>
                    <div className="preview-community-stats">
                      <div className="preview-stat">
                        <span className="preview-stat-icon">⭐</span>
                        <span>{previewModel.rating} rating ({previewModel.reviews} reviews)</span>
                      </div>
                      <div className="preview-stat">
                        <span className="preview-stat-icon">📥</span>
                        <span>{previewModel.downloads} downloads</span>
                      </div>
                      <div className="preview-stat">
                        <span className="preview-stat-icon">📅</span>
                        <span>Created {new Date(previewModel.created).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {previewTab === 'reviews' && (
                <div className="preview-reviews">
                  {getModelReviews(previewModel.name).length === 0 ? (
                    <div className="no-reviews">
                      <div className="no-reviews-icon">📝</div>
                      <h3>No Reviews Yet</h3>
                      <p>Be the first to review this model!</p>
                    </div>
                  ) : (
                    <div className="reviews-list">
                      {getModelReviews(previewModel.name).map(review => (
                        <div key={review.id} className="review-card">
                          <div className="review-header">
                            <div className="review-rating">
                              {getRatingStars(review.rating)}
                              <span 
                                className="rating-number"
                                style={{ color: getRatingColor(review.rating) }}
                              >
                                {review.rating}/5
                              </span>
                            </div>
                          </div>

                          <div className="review-content">
                            <h4 className="review-title">{review.title}</h4>
                            <p className="review-comment">{review.comment}</p>
                          </div>

                          <div className="review-footer">
                            <div className="review-meta">
                              <span className="review-author">
                                👤 {review.reviewerName}
                                {review.verified && <span className="verified-badge">✓ Verified</span>}
                              </span>
                              <span className="review-date">
                                📅 {review.date.toLocaleDateString()}
                              </span>
                            </div>
                            <div className="review-actions">
                              <button className="helpful-btn">
                                👍 Helpful ({review.helpful})
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="preview-actions">
              <button 
                className="btn-secondary preview-close-btn"
                onClick={closePreview}
              >
                Close Preview
              </button>
              <button 
                className="btn-primary preview-use-btn"
                onClick={() => {
                  onSelectModel(previewModel);
                  closePreview();
                }}
              >
                {previewModel.price === 0 ? '📥 Use Model' : `💳 Purchase for $${previewModel.price}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommunityModels;